// client/src/Book.jsx
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Papa from "papaparse";
import { FaTimes, FaFileCsv } from "react-icons/fa";
import Select from "react-select";
import "./Style.css";
import { BackOfficeHeader } from "./GameSessionHeader";

export default function Book() {
  const { cardroom } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
    const permission = localStorage.getItem("permission");
  const dealerMode = Number(localStorage.getItem("dealer_mode") || 0);
  const [players, setPlayers] = useState([]);
  const [books, setBooks] = useState([]);
  const [loadingBooks, setLoadingBooks] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newBookName, setNewBookName] = useState("");
  const [bookError, setBookError] = useState("");
  const [expandedBookId, setExpandedBookId] = useState(null);

  // P2P State
  const [showP2PModal, setShowP2PModal] = useState(false);
  const [p2pPlayer1Id, setP2PPlayer1Id] = useState("");
  const [p2pPlayer2Id, setP2PPlayer2Id] = useState("");
  const [p2pAmount, setP2PAmount] = useState("");
  const [p2pError, setP2PError] = useState("");
  const [p2pSuccess, setP2PSuccess] = useState("");

  // Credit Limits State
  const [creditLimits, setCreditLimits] = useState([]);
  const [newLimitPlayerId, setNewLimitPlayerId] = useState("");
  const [newLimitAmount, setNewLimitAmount] = useState("");
  const [limitError, setLimitError] = useState("");
  const [limitSuccess, setLimitSuccess] = useState("");
  const [removingCreditLimitId, setRemovingCreditLimitId] = useState(null);

  const houseBook = {
    id: "house",
    name: "House",
    isHouse: true,
  };

  const allBooks = [houseBook, ...books];

  function fmtMoney(n) {
    const v = Number(n || 0);
    return (
      "$" +
      Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 0 })
    );
  }

  const exportToCSV = (title, headers, data, e) => {
    e.stopPropagation();
    if (!data || data.length === 0) return;
    const csv = Papa.unparse({
      fields: headers,
      data: data
    });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${title}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    if (!token) {
      navigate(`/`);
      return;
    }
    if (dealerMode === 2) {
      navigate(`/${cardroom}/dealer_dashboard`);
      return;
    } else if (!permission?.includes("V")){
      navigate('/');
      return;
    }
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }, [token, cardroom, navigate, dealerMode, permission]);

  useEffect(() => {
    async function loadData() {
      try {
        const { data: allPlayers } = await axios.get(
          `/${cardroom}/api/players`
        );
        setPlayers(allPlayers);
      } catch (err) {
        console.error("Load data failed", err);
        navigate(`/${cardroom}/login`);
      }
    }
    loadData();
  }, [cardroom, navigate]);

  useEffect(() => {
    async function loadBooks() {
      setLoadingBooks(true);
      try {
        const { data } = await axios.get(`/${cardroom}/api/books`);
        setBooks(data);
      } catch (err) {
        console.error("Failed to load books", err);
      } finally {
        setLoadingBooks(false);
      }
    }
    async function loadCreditLimits() {
      try {
        const { data } = await axios.get(`/${cardroom}/api/credit_limits`);
        setCreditLimits(data);
      } catch (err) {
        console.error("Failed to load credit limits", err);
      }
    }

    if (token) {
      Promise.all([loadBooks(), loadCreditLimits()]).finally(() => {
        setLoading(false);
      });
    }
  }, [cardroom, token]);

  const handleCreateBook = async (e) => {
    e.preventDefault();
    setBookError("");

    if (!newBookName.trim()) {
      setBookError("Book name is required");
      return;
    }

    try {
      const { data } = await axios.post(`/${cardroom}/api/books`, {
        name: newBookName.trim(),
      });

      setBooks((prev) => [...prev, { ...data, totalOwed: 0, playerCount: 0 }]);
      setNewBookName("");
    } catch (err) {
      setBookError(err.response?.data?.error || "Failed to create book");
    }
  };

  const handleSetCreditLimit = async (e) => {
    e.preventDefault();
    setLimitError("");
    setLimitSuccess("");

    if (!newLimitPlayerId) {
      setLimitError("Please select a player");
      return;
    }
    if (newLimitAmount === "" || Number(newLimitAmount) < 0) {
      setLimitError("Please enter a valid amount");
      return;
    }

    try {
      await axios.post(`/${cardroom}/api/credit_limits`, {
        player_id: newLimitPlayerId,
        credit_limit: Number(newLimitAmount),
      });

      const { data } = await axios.get(`/${cardroom}/api/credit_limits`);
      setCreditLimits(data);
      setLimitSuccess("Credit limit updated successfully");
      setNewLimitPlayerId("");
      setNewLimitAmount("");
      
      setTimeout(() => setLimitSuccess(""), 3000);
    } catch (err) {
      setLimitError(err.response?.data?.error || "Failed to set credit limit");
    }
  };

  const handleRemoveCreditLimit = async (limitId) => {
    if (removingCreditLimitId) return;
    setLimitError("");
    setLimitSuccess("");

    try {
      setRemovingCreditLimitId(limitId);
      await axios.delete(`/${cardroom}/api/credit_limits/${limitId}`);
      setCreditLimits((prev) => prev.filter((limit) => limit.id !== limitId));
      setLimitSuccess("Credit limit removed successfully");
      setTimeout(() => setLimitSuccess(""), 3000);
    } catch (err) {
      setLimitError(err.response?.data?.error || "Failed to remove credit limit");
    } finally {
      setRemovingCreditLimitId(null);
    }
  };

  const handleP2PTransaction = async (e) => {
    e.preventDefault();
    setP2PError("");
    setP2PSuccess("");

    if (!p2pPlayer1Id || !p2pPlayer2Id) {
      setP2PError("Both players must be selected.");
      return;
    }
    if (!p2pAmount || Number(p2pAmount) <= 0) {
      setP2PError("Please enter a valid amount.");
      return;
    }

    try {
      await axios.post(`/${cardroom}/api/p2p-book/transaction`, {
        senderId: p2pPlayer1Id,
        receiverId: p2pPlayer2Id,
        amount: Number(p2pAmount)
      });

      // Reload players and books to refresh balances
      const [playersRes, booksRes] = await Promise.all([
        axios.get(`/${cardroom}/api/players`),
        axios.get(`/${cardroom}/api/books`)
      ]);
      setPlayers(playersRes.data);
      setBooks(booksRes.data);

      setP2PSuccess("P2P transaction completed successfully.");
      setP2PPlayer1Id("");
      setP2PPlayer2Id("");
      setP2PAmount("");

      setTimeout(() => setP2PSuccess(""), 3000);
      setShowP2PModal(false);
    } catch (err) {
      setP2PError(err.response?.data?.error || "Failed to complete P2P transaction");
    }
  };

  const getPlayersForBook = (bookId) => {
    if (bookId === "house") {
      // House book includes players with no book_id or book_id is null
      return players.filter(
        (p) =>
          (!p.book_id || p.book_id === null) &&
          (Number(p.book || 0) !== 0 || Number(p.session_credit || 0) !== 0)
      );
    }
    return players.filter(
      (p) =>
        p.book_id === bookId &&
        (Number(p.book || 0) !== 0 || Number(p.session_credit || 0) !== 0)
    );
  };

  // Calculate overall totals
  const bookPlayers = (players || []).filter(
    (p) => (Number(p.book) || 0) !== 0 || (Number(p.session_credit) || 0) !== 0
  );
  const totalRunningDebt = bookPlayers.reduce(
    (sum, p) => sum + Number(p.book || 0),
    0
  );
  const totalOwed = bookPlayers.reduce(
    (sum, p) => sum + Number(p.session_credit || 0),
    0
  );
  const totalCombined = totalRunningDebt + totalOwed;
// Add after line 129 (after totalCombined calculation):
const totalOwedToPlayers = bookPlayers.reduce(
  (sum, p) => {
    const playerTotal = Number(p.book || 0);
    return playerTotal < 0 ? sum + Math.abs(playerTotal) : sum;
  },
  0
);

const totalOwedFromPlayers = bookPlayers.reduce(
  (sum, p) => {
    const playerTotal = Number(p.book || 0);
    return playerTotal > 0 ? sum + playerTotal : sum;
  },
  0
);

const bookOwedToPlayers = bookPlayers.reduce(
  (sum, p) => {
    const playerTotal = Number(p.book || 0);
    return playerTotal < 0 ? sum + Math.abs(playerTotal) : sum;
  },
  0
);

const bookOwedFromPlayers = bookPlayers.reduce(
  (sum, p) => {
    const playerTotal = Number(p.book || 0);
    return playerTotal > 0 ? sum + playerTotal : sum;
  },
  0
);

// P2P Data Helpers
const p2pPlayers = players.filter((p) => Number(p.book || 0) !== 0);

const p2pPlayer1Options = p2pPlayers.map((p) => ({
  value: p.id,
  label: `${p.name} (${fmtMoney(p.book)})`,
  book: Number(p.book || 0),
}));

const selectedP2PPlayer1 = p2pPlayers.find(p => p.id === p2pPlayer1Id);
const p1BookSign = selectedP2PPlayer1 ? Math.sign(Number(selectedP2PPlayer1.book || 0)) : 0;

const p2pPlayer2Options = p2pPlayers
  .filter((p) => Math.sign(Number(p.book || 0)) === (p1BookSign * -1) && p1BookSign !== 0)
  .map((p) => ({
    value: p.id,
    label: `${p.name} (${fmtMoney(p.book)})`,
    book: Number(p.book || 0),
  }));

const selectedP2PPlayer2 = p2pPlayers.find(p => p.id === p2pPlayer2Id);

// Max allowed based on min(abs(p1Book), abs(p2Book))
const p2pMaxAmount = 
  selectedP2PPlayer1 && selectedP2PPlayer2 
  ? Math.min(Math.abs(Number(selectedP2PPlayer1.book || 0)), Math.abs(Number(selectedP2PPlayer2.book || 0)))
  : 0;

  return (
    <div className="game">
      <BackOfficeHeader
        activePage="book"
        cardroom={cardroom}
        centerTitle="Book Manager"
        dealerMode={dealerMode}
        title="Book Manager"
      />
{/* MAIN CONTENT */}
      <div className="game-container">
        {loading ? (
          <div className="loading-container" style={{ padding: "4rem", textAlign: "center", color: "white" }}>
            <h2 style={{ marginBottom: "1.5rem", fontWeight: "700" }}>Gathering Data...</h2>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div className="spinner" style={{ border: "4px solid rgba(255,255,255,0.1)", borderTop: "4px solid #6366f1", borderRadius: "50%", width: "50px", height: "50px", animation: "spin 1s linear infinite" }}></div>
            </div>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
          </div>
        ) : (
          <>
        {/* Action Row: Create Book & P2P */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
          <section className="game-section" style={{ marginBottom: "2rem" }}>
            <h2 className="game-section__title">Create New Book</h2>
            <form onSubmit={handleCreateBook} className="game-form">
              <input
                type="text"
                className="game-input"
                placeholder="Book name"
                value={newBookName}
                onChange={(e) => setNewBookName(e.target.value)}
                required
                style={{ maxWidth: "300px" }}
              />
              <button type="submit" className="game-btn game-btn--primary">
                Create
              </button>
            </form>
            {bookError && (
              <p className="game-text-danger" style={{ marginTop: "1rem" }}>
                {bookError}
              </p>
            )}
          </section>

          <section className="game-section" style={{ marginBottom: "2rem" }}>
            <h2 className="game-section__title">Player 2 Player Transaction</h2>
            <div style={{ display: "flex", alignItems: "flex-end", gap: "1rem" }}>
              <div style={{ flex: 1, maxWidth: "300px" }}>
                <Select
                  options={p2pPlayer1Options}
                  value={p2pPlayer1Options.find((opt) => opt.value === p2pPlayer1Id) || null}
                  onChange={(opt) => {
                    setP2PPlayer1Id(opt ? opt.value : "");
                    setP2PPlayer2Id(""); // Reset P2 when P1 changes
                    setP2PAmount("");
                  }}
                  placeholder="Select Player 1..."
                  isClearable
                  classNamePrefix="react-select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      background: "var(--game-bg-transparent)",
                      borderColor: "var(--game-border)",
                      color: "var(--game-text-primary)",
                      minHeight: "42px",
                      cursor: "pointer",
                    }),
                    menu: (base) => ({
                      ...base,
                      background: "var(--game-surface)",
                      border: "1px solid var(--game-border)",
                      zIndex: 9999,
                    }),
                    option: (base, state) => {
                      const bookAmt = Number(state.data.book || 0);
                      let textColor = "var(--game-text-primary)";
                      if (bookAmt > 0) textColor = "var(--game-danger)";
                      else if (bookAmt < 0) textColor = "var(--game-success)";
                      return {
                        ...base,
                        background: state.isFocused
                          ? "var(--game-surface-hover)"
                          : "var(--game-surface)",
                        color: textColor,
                        cursor: "pointer",
                      };
                    },
                    singleValue: (base) => ({
                      ...base,
                      color: "var(--game-text-primary)",
                    }),
                    input: (base) => ({
                      ...base,
                      color: "var(--game-text-primary)",
                    }),
                  }}
                />
              </div>
              {selectedP2PPlayer1 && (
                <div style={{ display: "flex", alignItems: "center" }}>
                  {p1BookSign < 0 ? (
                    <span className="game-badge game-badge--credit">
                      <b>${Math.abs(selectedP2PPlayer1.book)}</b>&nbsp;Account Balance
                    </span>
                  ) : p1BookSign > 0 ? (
                    <span className="game-badge game-badge--debt">
                      <b>${selectedP2PPlayer1.book}</b>&nbsp;Account Debt
                    </span>
                  ) : null}
                </div>
              )}
              <button
                className="game-btn game-btn--primary"
                onClick={() => setShowP2PModal(true)}
                disabled={!p2pPlayer1Id}
              >
                P2P
              </button>
            </div>
          </section>
        </div>

        {/* Overall Stats */}
        <section className="game-section">
          <h2 className="game-section__title">Overall Statistics</h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "1rem",
            }}
          >
            <div
              style={{
                background: "var(--game-bg-tertiary)",
                border: "1px solid var(--game-border)",
                borderRadius: "var(--game-radius)",
                padding: "1rem",
              }}
            >
              <div
                style={{
                  fontSize: "0.875rem",
                  color: "var(--game-text-secondary)",
                  marginBottom: "0.5rem",
                }}
              >
                Combined Session Credit
              </div>
              <div
                style={{
                  fontSize: "1.5rem",
                  fontWeight: 700,
                  color:
                    totalOwed < 0
                      ? "var(--game-success)"
                      : totalOwed === 0
                      ? "var(--game-text-primary)"
                      : "var(--game-danger)",
                }}
              >
                {fmtMoney(totalOwed)}
              </div>
            </div>

            <div
              style={{
                background: "var(--game-bg-tertiary)",
                border: "1px solid var(--game-border)",
                borderRadius: "var(--game-radius)",
                padding: "1rem",
              }}
            >
              <div
                style={{
                  fontSize: "0.875rem",
                  color: "var(--game-text-secondary)",
                  marginBottom: "0.5rem",
                }}
              >
                Account Debt
              </div>
<div
  style={{
    fontSize: "1.5rem",
    fontWeight: 700,
    color:
      totalRunningDebt < 0
        ? "var(--game-success)"
        : totalRunningDebt === 0
        ? "var(--game-text-primary)"
        : "var(--game-danger)",
  }}
>
  {fmtMoney(totalRunningDebt)}
  {totalOwedToPlayers !== 0 && totalOwedFromPlayers !== 0 && (
    <span
      style={{
        fontSize: "1rem",
        fontWeight: 500,
        marginLeft: "0.5rem",
        color: "var(--game-text-secondary)",
      }}
    >
      (
      <span style={{ color: "var(--game-success)" }}>
        {fmtMoney(totalOwedToPlayers)}
      </span>{" "}
      –{" "}
      <span style={{ color: "var(--game-danger)" }}>
        {fmtMoney(totalOwedFromPlayers)}
      </span>
      )
    </span>
  )}
</div>

            </div>

            <div
              style={{
                background: "var(--game-bg-tertiary)",
                border: "1px solid var(--game-border)",
                borderRadius: "var(--game-radius)",
                padding: "1rem",
              }}
            >
              <div
                style={{
                  fontSize: "0.875rem",
                  color: "var(--game-text-secondary)",
                  marginBottom: "0.5rem",
                }}
              >
                Combined Total
              </div>
              <div
                style={{
                  fontSize: "1.5rem",
                  fontWeight: 700,
                  color:
                    totalCombined < 0
                      ? "var(--game-success)"
                      : totalCombined === 0
                      ? "var(--game-text-primary)"
                      : "var(--game-danger)",
                }}
              >
                {fmtMoney(totalCombined)}
              </div>
            </div>
          </div>
        </section>

        {/* Books List */}
        <section className="game-section">
          <h2 className="game-section__title">All Books ({allBooks.length})</h2>
          {loadingBooks ? (
            <p className="game-text-secondary">Loading books...</p>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
              }}
            >
              {allBooks.map((book) => {
                const bookPlayers = getPlayersForBook(book.id);
                const bookTotal = bookPlayers.reduce(
                  (sum, p) =>
                    sum + Number(p.book || 0) + Number(p.session_credit || 0),
                  0
                );
                const isExpanded = expandedBookId === book.id;

                return (
                  <div
                    key={book.id}
                    style={{
                      border: "1px solid var(--game-border)",
                      borderRadius: "var(--game-radius)",
                      overflow: "hidden",
                      background: "var(--game-bg-tertiary)",
                    }}
                  >
                    {/* Book Header - Clickable */}
                    <div
                      onClick={() =>
                        setExpandedBookId(isExpanded ? null : book.id)
                      }
                      style={{
                        padding: "1rem",
                        background: isExpanded
                          ? "var(--game-surface)"
                          : "var(--game-bg-tertiary)",
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        transition: "var(--game-transition)",
                      }}
                      onMouseEnter={(e) => {
                        if (!isExpanded)
                          e.currentTarget.style.background =
                            "var(--game-surface-hover)";
                      }}
                      onMouseLeave={(e) => {
                        if (!isExpanded)
                          e.currentTarget.style.background =
                            "var(--game-bg-tertiary)";
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: "1.1rem",
                            color: "var(--game-text-primary)",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem"
                          }}
                        >
                          {book.name}
                          {isExpanded && bookPlayers.length > 0 && (
                            <FaFileCsv
                              className="game-icon-btn game-icon-btn--success"
                              onClick={(e) => {
                                const data = bookPlayers.map(player => {
                                  const playerTotal = Number(player.book || 0) + Number(player.session_credit || 0);
                                  return {
                                    Player: player.name,
                                    "Session Credit": player.session_credit || 0,
                                    "Account Debt": player.book || 0,
                                    Total: playerTotal
                                  };
                                });
                                exportToCSV(`${book.name}_Book`, Object.keys(data[0] || {}), data, e);
                              }}
                            />
                          )}
                        </div>
                        <div
                          style={{
                            fontSize: "0.875rem",
                            color: "var(--game-text-secondary)",
                            marginTop: "0.25rem",
                          }}
                        >
                          {bookPlayers.length} player
                          {bookPlayers.length !== 1 ? "s" : ""}
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div
                          style={{
                            fontSize: "0.75rem",
                            color: "var(--game-text-secondary)",
                          }}
                        >
                          {bookTotal < 0
                            ? "Total Owed to Players"
                            : "Total Owed from Players"}
                        </div>

<div
  style={{
    fontSize: "1.25rem",
    fontWeight: 700,
    color:
      bookTotal < 0
        ? "var(--game-success)"
        : bookTotal === 0
        ? "var(--game-text-primary)"
        : "var(--game-danger)",
  }}
>
  {fmtMoney(bookTotal)}
  {bookOwedToPlayers !== 0 && bookOwedFromPlayers !== 0 && (
    <span
      style={{
        fontSize: "0.875rem",
        fontWeight: 500,
        marginLeft: "0.35rem",
        color: "var(--game-text-secondary)",
      }}
    >
      (
      <span style={{ color: "var(--game-success)" }}>
        {fmtMoney(bookOwedToPlayers)}
      </span>{" "}
      –{" "}
      <span style={{ color: "var(--game-danger)" }}>
        {fmtMoney(bookOwedFromPlayers)}
      </span>
      )
    </span>
  )}
</div>

                      </div>
                    </div>

                    {/* Expanded Player List */}
                    {isExpanded && bookPlayers.length > 0 && (
                      <div
                        style={{ borderTop: "1px solid var(--game-border)" }}
                      >
                        <div style={{ overflowX: "auto" }}>
                          <table
                            style={{
                              width: "100%",
                              borderCollapse: "collapse",
                            }}
                          >
                            <thead>
                              <tr style={{ background: "var(--game-surface)" }}>
                                <th
                                  style={{
                                    textAlign: "center",
                                    padding: "0.75rem",
                                    fontSize: "0.875rem",
                                    color: "var(--game-text-secondary)",
                                    fontWeight: 500,
                                  }}
                                >
                                  Player
                                </th>
                                <th
                                  style={{
                                    textAlign: "center",
                                    padding: "0.75rem",
                                    fontSize: "0.875rem",
                                    color: "var(--game-text-secondary)",
                                    fontWeight: 500,
                                  }}
                                >
                                  Session Credit
                                </th>
                                <th
                                  style={{
                                    textAlign: "center",
                                    padding: "0.75rem",
                                    fontSize: "0.875rem",
                                    color: "var(--game-text-secondary)",
                                    fontWeight: 500,
                                  }}
                                >
                                  Account Debt
                                </th>
                                <th
                                  style={{
                                    textAlign: "center",
                                    padding: "0.75rem",
                                    fontSize: "0.875rem",
                                    color: "var(--game-text-secondary)",
                                    fontWeight: 500,
                                  }}
                                >
                                  Total
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {bookPlayers.map((player) => {
                                const playerTotal =
                                  Number(player.book || 0) +
                                  Number(player.session_credit || 0);
                                return (
                                  <tr
                                    key={player.id}
                                    style={{
                                      borderBottom:
                                        "1px solid var(--game-border-light)",
                                    }}
                                  >
                                    <td
                                      style={{
                                        padding: "0.75rem",
                                        color: "var(--game-text-primary)",
                                      }}
                                    >
                                      {player.name}
                                    </td>
                                    <td
                                      style={{
                                        padding: "0.75rem",
                                        textAlign: "center",
                                        color:
                                          Number(player.session_credit || 0) < 0
                                            ? "var(--game-success)"
                                            : Number(
                                                player.session_credit || 0
                                              ) === 0
                                            ? "var(--game-text-primary)"
                                            : "var(--game-danger)",
                                      }}
                                    >
                                      {fmtMoney(player.session_credit)}
                                    </td>
                                    <td
                                      style={{
                                        padding: "0.75rem",
                                        textAlign: "center",
                                        color:
                                          Number(player.book || 0) < 0
                                            ? "var(--game-success)"
                                            : Number(player.book || 0) === 0
                                            ? "var(--game-text-primary)"
                                            : "var(--game-danger)",
                                      }}
                                    >
                                      {fmtMoney(player.book)}
                                    </td>
                                    <td
                                      style={{
                                        padding: "0.75rem",
                                        textAlign: "center",
                                        fontWeight: 600,
                                        color:
                                          playerTotal < 0
                                            ? "var(--game-success)"
                                            : "var(--game-danger)",
                                      }}
                                    >
                                      {fmtMoney(playerTotal)}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {isExpanded && bookPlayers.length === 0 && (
                      <div
                        style={{
                          padding: "1rem",
                          textAlign: "center",
                          color: "var(--game-text-secondary)",
                          fontSize: "0.875rem",
                          borderTop: "1px solid var(--game-border)",
                        }}
                      >
                        No players with outstanding balances in this book.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Credit Limits Section */}
        <section className="game-section">
          <h2 className="game-section__title">Credit Limits</h2>
          
          {/* Add Limit Form */}
          <div style={{ background: "var(--game-bg-tertiary)", padding: "1.5rem", borderRadius: "var(--game-radius)", border: "1px solid var(--game-border)", marginBottom: "1.5rem" }}>
            <h3 style={{ fontSize: "1.1rem", marginBottom: "1rem", color: "var(--game-text-primary)" }}>Set or Update Limit</h3>
            <form onSubmit={handleSetCreditLimit} style={{ display: "flex", gap: "1rem", alignItems: "flex-end", flexWrap: "wrap" }}>
              <div style={{ flex: ".4 .4 250px" }}>
                <label className="game-label">Select Player</label>
                <Select
                  options={players.map(p => ({ value: p.id, label: p.name }))}
                  value={players.map(p => ({ value: p.id, label: p.name })).find(o => o.value === newLimitPlayerId) || null}
                  onChange={(option) => setNewLimitPlayerId(option ? option.value : "")}
                  placeholder="Choose player..."
                  isSearchable
                  classNamePrefix="select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      backgroundColor: "var(--game-surface)",
                      borderColor: "var(--game-border)",
                      minHeight: "42px",
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: "var(--game-surface)",
                      zIndex: 100,
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isFocused ? "var(--game-surface-hover)" : "var(--game-surface)",
                      color: "var(--game-text-primary)",
                      cursor: "pointer",
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: "var(--game-text-primary)",
                    }),
                    input: (base) => ({
                      ...base,
                      color: "var(--game-text-primary)",
                    }),
                  }}
                />
              </div>
              <div style={{ flex: "0 1 150px" }}>
                <label className="game-label">Limit Amount ($)</label>
                <input
                  type="number"
                  className="game-input"
                  min="0"
                  step="1"
                  value={newLimitAmount}
                  onChange={(e) => setNewLimitAmount(e.target.value)}
                  placeholder="e.g. 1000"
                  required
                />
              </div>
              <button type="submit" className="game-btn game-btn--primary" style={{ height: "42px" }} disabled={!permission?.includes("C")}>
                Save Limit
              </button>
            </form>
            {limitError && <p className="game-text-danger" style={{ marginTop: "1rem", marginBottom: 0 }}>{limitError}</p>}
            {limitSuccess && <p className="game-text-success" style={{ marginTop: "1rem", marginBottom: 0 }}>{limitSuccess}</p>}
          </div>

          {/* Limits List */}
          {creditLimits.length === 0 ? (
            <p className="game-text-secondary" style={{ textAlign: "center", padding: "2rem", background: "var(--game-bg-tertiary)", borderRadius: "var(--game-radius)", border: "1px solid var(--game-border)" }}>
              No credit limits have been set yet.
            </p>
          ) : (
            <div style={{ overflowX: "auto", background: "var(--game-bg-tertiary)", borderRadius: "var(--game-radius)", border: "1px solid var(--game-border)" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "var(--game-surface)", borderBottom: "1px solid var(--game-border)" }}>
                    <th style={{ padding: "1rem", textAlign: "center", color: "var(--game-text-secondary)", fontWeight: 500 }}>Player</th>
                    <th style={{ padding: "1rem", textAlign: "center", color: "var(--game-text-secondary)", fontWeight: 500 }}>Session Credit</th>
                    <th style={{ padding: "1rem", textAlign: "center", color: "var(--game-text-secondary)", fontWeight: 500 }}>Account Debt</th>
                    <th style={{ padding: "1rem", textAlign: "center", color: "var(--game-text-secondary)", fontWeight: 500 }}>Credit Limit</th>
                    <th style={{ padding: "1rem", textAlign: "center", color: "var(--game-text-secondary)", fontWeight: 500 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {creditLimits.map((limit) => {
                    const sessionCredit = Number(limit.session_credit || 0);
                    const bookNum = Number(limit.book || 0);
                    const limitAmount = Number(limit.credit_limit || 0);
                    
                    const utilized = sessionCredit + bookNum;
                    const progressPercent = Math.max(0, Math.min(100, (utilized / limitAmount) * 100));
                    const textDisplay = utilized < 0 ? `-$${Math.abs(utilized)} / $${limitAmount}` : `$${utilized} / $${limitAmount}`;

                    return (
                      <tr key={limit.id} style={{ borderBottom: "1px solid var(--game-border-light)" }}>
                        <td style={{ padding: "1rem", textAlign: "center", color: "var(--game-text-primary)", fontWeight: 500 }}>{limit.name}</td>
                        <td style={{ padding: "1rem", textAlign: "center", color: sessionCredit > 0 ? "var(--game-session-yellow)" : "var(--game-text-primary)", fontWeight: 500 }}>
                          {sessionCredit > 0 ? `$${sessionCredit}` : `$0`}
                        </td>
                        <td style={{ padding: "1rem", textAlign: "center", color: bookNum < 0 ? "var(--game-success)" : bookNum > 0 ? "var(--game-danger)" : "var(--game-text-primary)", fontWeight: 500 }}>
                          {bookNum < 0 ? `-$${Math.abs(bookNum)}` : `$${bookNum}`}
                        </td>
                        <td style={{ padding: "1rem" }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
                            <div style={{ width: '100px', backgroundColor: '#eee', height: '8px', borderRadius: '4px', overflow: 'hidden' }}>
                              <div style={{ width: `${progressPercent}%`, backgroundColor: 'var(--game-danger)', height: '100%' }}></div>
                            </div>
                            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: "var(--game-text-primary)", minWidth: '70px', textAlign: 'right' }}>
                                {textDisplay}
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: "1rem", textAlign: "center" }}>
                          <button
                            type="button"
                            className="game-btn game-btn--danger game-btn--sm"
                            onClick={() => handleRemoveCreditLimit(limit.id)}
                            disabled={!permission?.includes("C") || removingCreditLimitId === limit.id}
                          >
                            {removingCreditLimitId === limit.id ? "Removing..." : "Remove"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
          </>
        )}
      </div>

      {/* P2P Modal */}
      {showP2PModal && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => setShowP2PModal(false)}
          />
          <div className="game-modal" style={{ maxWidth: "500px" }}>
            <div className="game-modal__header">
              <h3 className="game-modal__title">P2P Transaction</h3>
              <button
                className="game-icon-btn"
                onClick={() => setShowP2PModal(false)}
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="game-modal__body">
              <form onSubmit={handleP2PTransaction} className="game-form-vertical">
                <div>
                  <label className="game-label">Player 1</label>
                  <div style={{
                    padding: "1rem",
                    background: "var(--game-bg-tertiary)",
                    border: "1px solid var(--game-border)",
                    borderRadius: "var(--game-radius)",
                    color: "var(--game-text-primary)",
                    display: "flex",
                    alignItems: "center",
                    gap: "1rem"
                  }}>
                    <div style={{ fontWeight: 600, fontSize: "1.1rem" }}>
                      {selectedP2PPlayer1?.name} 
                    </div>
                    <div>
                        {p1BookSign < 0 ? (
                          <span className="game-badge game-badge--credit">
                            <b>${Math.abs(selectedP2PPlayer1?.book)}</b>&nbsp;Account Balance
                          </span>
                        ) : p1BookSign > 0 ? (
                          <span className="game-badge game-badge--debt">
                            <b>${selectedP2PPlayer1?.book}</b>&nbsp;Account Debt
                          </span>
                        ) : null}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="game-label">Player 2</label>
                  <Select
                    options={p2pPlayer2Options}
                    value={p2pPlayer2Options.find((opt) => opt.value === p2pPlayer2Id) || null}
                    onChange={(opt) => {
                      setP2PPlayer2Id(opt ? opt.value : "");
                      setP2PAmount(""); // Reset amount when player 2 changes
                    }}
                    placeholder="Select Player 2 (Opposite Book)..."
                    isClearable
                    className="game-select"
                    classNamePrefix="select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        backgroundColor: "#2d3748",
                        borderColor: "#4a5568",
                        minWidth: "100%",
                      }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: "#2d3748",
                        zIndex: 9999,
                      }),
                      option: (base, state) => {
                        const bookAmt = Number(state.data.book || 0);
                        let textColor = "#e2e8f0";
                        if (bookAmt > 0) textColor = "var(--game-danger)";
                        else if (bookAmt < 0) textColor = "var(--game-success)";
                        return {
                          ...base,
                          backgroundColor: state.isFocused ? "#4a5568" : "#2d3748",
                          color: textColor,
                          cursor: "pointer",
                        };
                      },
                      singleValue: (base) => ({
                        ...base,
                        color: "#e2e8f0",
                      }),
                      input: (base) => ({
                        ...base,
                        color: "#e2e8f0",
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "#718096",
                      }),
                    }}
                  />
                  {selectedP2PPlayer2 && (
                    <div style={{ marginTop: "0.75rem", display: "flex" }}>
                      {Math.sign(Number(selectedP2PPlayer2.book)) < 0 ? (
                        <span className="game-badge game-badge--credit">
                          <b>${Math.abs(selectedP2PPlayer2.book)}</b>&nbsp;Account Balance
                        </span>
                      ) : (
                        <span className="game-badge game-badge--debt">
                          <b>${selectedP2PPlayer2.book}</b>&nbsp;Account Debt
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {selectedP2PPlayer2 && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', alignItems: 'end' }}>
                    <div>
                      <label className="game-label">
                        Amount
                      </label>
                      <input
                        type="number"
                        className="game-input"
                        value={p2pAmount}
                        onChange={(e) => {
                          const val = Number(e.target.value);
                          if (val > p2pMaxAmount) {
                            setP2PAmount(p2pMaxAmount);
                          } else if (val < 0) {
                            setP2PAmount(0);
                          } else {
                            setP2PAmount(e.target.value);
                          }
                        }}
                        max={p2pMaxAmount}
                        min={0}
                        step="1"
                        required
                        placeholder={`Max: ${fmtMoney(p2pMaxAmount)}`}
                      />
                    </div>
                  </div>
                )}

                {p2pError && <div className="game-text-danger">{p2pError}</div>}
                {p2pSuccess && <div style={{ color: "var(--game-success)" }}>{p2pSuccess}</div>}

                <div className="game-modal__footer" style={{ marginTop: "1.5rem" }}>
                  <button
                    type="button"
                    className="game-btn game-btn--ghost"
                    onClick={() => setShowP2PModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="game-btn game-btn--primary"
                    disabled={!p2pPlayer2Id || !p2pAmount || Number(p2pAmount) <= 0}
                  >
                    Complete Transaction
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
